import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Tabs,
    Tab,
    Table,
    Accordion,
    Alert,
    Modal,
    Form,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import assignmentService from '@/shared/services/assignmentService';
import termService from '@/shared/services/termService';
import programService from '@/shared/services/programService';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { PROGRAM_PAGE_URLS } from '@/shared/utils/programInfo';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';
import EmptyState from '@/components/common/EmptyState';
import StatValue from '@/components/common/StatValue';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { applyEntryHref } from '@/shared/utils/positionsAccess';
import {
    formatDeadlineDay,
    formatWindowDate,
    isBeforeOpen,
} from '@/shared/utils/deadline';
import AssignmentStartNote from './AssignmentStartNote';

const NOT_A_TIME_CARD =
    'This is your own record. It is not a time card, and nothing here affects your pay.';

// How many journal entries a card shows before "Show all"
const JOURNAL_PREVIEW = 3;

// Today in the student's own time zone, as the date input wants it
const todayLocal = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const emptyJournalForm = () => ({
    date: todayLocal(),
    notes: '',
    wentWell: '',
});

// Newest first; entries on the same day keep the order they were written
const newestFirst = (entries = []) =>
    [...entries].sort(
        (a, b) =>
            new Date(b.date) - new Date(a.date) ||
            new Date(b.loggedAt || 0) - new Date(a.loggedAt || 0)
    );

const PROGRAM_CONFIG = {
    si: {
        bg: 'success',
        label: 'SI',
        full: 'Supplemental Instruction',
        color: '#2E7D32',
    },
    ta: {
        bg: 'primary',
        label: 'TA',
        full: 'Teaching Assistant',
        color: '#1565C0',
    },
    la: {
        bg: '',
        label: 'LA',
        full: 'Learning Assistant',
        color: 'var(--psh-la-color)',
        badgeClass: 'badge-la',
    },
};

const STATUS_CONFIG = {
    active: { bg: 'success', label: 'Active' },
    pending: { bg: 'warning', label: 'Pending Start' },
    completed: { bg: 'secondary', label: 'Completed' },
    terminated: { bg: 'danger', label: 'Terminated' },
};

function AssignmentsPage() {
    const hiringEnabled = useFeature('hiring');
    const studentApplicationEnabled = useFeature('studentApplication');
    const { userProfile, effectiveRoles } = useAuth();
    // An assignment starts from a position, so a student is sent to Open
    // Positions when its route admits them, else the catalog.
    const findWorkHref = applyEntryHref({
        hiring: hiringEnabled,
        studentApplication: studentApplicationEnabled,
        roles: resolveRoles({ effectiveRoles, userProfile }),
        fallback: '/courses',
    });
    const [assignments, setAssignments] = useState({ current: [], past: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [activeTab, setActiveTab] = useState('current');
    const [termMap, setTermMap] = useState({});

    // Journal entry modal state
    const [showJournalModal, setShowJournalModal] = useState(false);
    const [journalAssignment, setJournalAssignment] = useState(null);
    const [journalForm, setJournalForm] = useState(emptyJournalForm);
    const [journalError, setJournalError] = useState(null);
    const [notesMissing, setNotesMissing] = useState(false);
    // Focus moves here when it is empty, so a screen reader hears why
    const notesRef = useRef(null);
    const [savingEntry, setSavingEntry] = useState(false);
    // Cards showing their whole journal, by assignment id
    const [expandedJournals, setExpandedJournals] = useState({});
    // The LA program's contact, for the line on LA assignments
    const [laContactEmail, setLaContactEmail] = useState(null);

    useEffect(() => {
        const loadAssignments = async () => {
            try {
                setLoading(true);
                setError(null);
                setLoadFailed(false);
                const [data, terms] = await Promise.all([
                    assignmentService.getMyAssignments(),
                    termService.getAllTerms().catch(() => []),
                ]);
                setAssignments(data);
                const map = {};
                terms.forEach((t) => {
                    map[t.code] = t;
                });
                setTermMap(map);
            } catch (_err) {
                setLoadFailed(true);
            } finally {
                setLoading(false);
            }
        };

        loadAssignments();
    }, [reloadKey]);

    const hasLAAssignment = (assignments.current || []).some(
        (a) => a.programType === 'la'
    );

    // Only fetched when there is an LA card to show it on. Without it the
    // line links to the LA program page instead.
    useEffect(() => {
        if (!hasLAAssignment) return undefined;
        let cancelled = false;
        programService
            .getProgramInfo('la')
            .then((info) => {
                if (!cancelled) setLaContactEmail(info?.contactEmail || null);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [hasLAAssignment]);

    const handleOpenJournal = (assignment) => {
        setJournalAssignment(assignment);
        setJournalForm(emptyJournalForm());
        setJournalError(null);
        setNotesMissing(false);
        setShowJournalModal(true);
    };

    // Stays open while saving, so a failed save is never shown in a closed
    // modal and a late reply can't close a modal opened after it
    const closeJournal = () => {
        if (!savingEntry) setShowJournalModal(false);
    };

    const updateJournalForm = (field) => (e) => {
        const { value } = e.target;
        setJournalForm((prev) => ({ ...prev, [field]: value }));
        if (field === 'notes' && value.trim()) setNotesMissing(false);
    };

    const handleSubmitJournal = async (e) => {
        e.preventDefault();
        if (!journalAssignment) return;

        if (!journalForm.notes.trim()) {
            setNotesMissing(true);
            notesRef.current?.focus();
            return;
        }
        if (!journalForm.date) {
            setJournalError('Please choose a date.');
            return;
        }

        try {
            setSavingEntry(true);
            setJournalError(null);

            await assignmentService.updateAssignment(journalAssignment._id, {
                logEntry: {
                    date: journalForm.date,
                    notes: journalForm.notes.trim(),
                    wentWell: journalForm.wentWell.trim() || undefined,
                },
            });
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to save your journal entry. Please try again.';
            setJournalError(message);
            setSavingEntry(false);
            return;
        }

        setShowJournalModal(false);
        setJournalAssignment(null);
        setSavingEntry(false);

        // Reload so the new entry shows in the journal. The entry is already
        // saved, so a failed reload must not say it wasn't.
        try {
            const data = await assignmentService.getMyAssignments();
            setAssignments(data);
        } catch (_err) {
            setError(
                'Your journal entry was saved, but the page could not refresh. Reload the page to see it.'
            );
        }
    };

    const journalCount = (list) =>
        list.reduce((sum, a) => sum + (a.hourLog?.length || 0), 0);

    const renderJournal = (assignment, { showNote }) => {
        const entries = newestFirst(assignment.hourLog);
        const expanded = !!expandedJournals[assignment._id];
        const shown = expanded ? entries : entries.slice(0, JOURNAL_PREVIEW);
        const listId = `journal-${assignment._id}`;
        return (
            <section
                className="mt-3"
                aria-label={`Activity journal for ${assignment.course?.title || getCourseDisplay(assignment)}`}
            >
                <h6 className="text-muted mb-1">Activity journal</h6>
                {showNote && (
                    <p className="small text-muted mb-2">{NOT_A_TIME_CARD}</p>
                )}
                {entries.length === 0 ? (
                    <p className="small mb-0">No journal entries yet.</p>
                ) : (
                    <ul id={listId} className="list-unstyled small mb-0">
                        {shown.map((entry, idx) => (
                            <li
                                key={entry._id || idx}
                                className="border-start border-2 ps-2 mb-2"
                            >
                                <div className="fw-medium">
                                    {formatWindowDate(entry.date)}
                                </div>
                                {entry.notes ? (
                                    <div className="text-break">
                                        {entry.notes}
                                    </div>
                                ) : (
                                    <div className="text-muted">
                                        No description
                                    </div>
                                )}
                                {entry.wentWell && (
                                    <div className="text-muted text-break">
                                        What went well: {entry.wentWell}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {entries.length > JOURNAL_PREVIEW && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        aria-expanded={expanded}
                        aria-controls={listId}
                        onClick={() =>
                            setExpandedJournals((prev) => ({
                                ...prev,
                                [assignment._id]: !expanded,
                            }))
                        }
                    >
                        {expanded
                            ? 'Show fewer entries'
                            : `Show all ${entries.length} entries`}
                    </Button>
                )}
            </section>
        );
    };

    const getCourseDisplay = (assignment) => {
        if (assignment.course) {
            return `${assignment.course.subject} ${assignment.course.courseNumber}`;
        }
        return 'Unknown Course';
    };

    // The request's own section when it has one, so the heading and the
    // section line below it read the same section
    const sectionOf = (assignment) =>
        assignment.section?.section || assignment.course?.section;

    const renderCurrentAssignmentCard = (assignment) => {
        const programConfig =
            PROGRAM_CONFIG[assignment.programType] || PROGRAM_CONFIG.si;
        const startDate = termMap[assignment.term]?.startDate;
        const notStarted = Boolean(startDate) && isBeforeOpen(startDate);
        return (
            <Card key={assignment._id} className="mb-4 border-0 shadow-sm">
                <Card.Header
                    className="bg-white border-0 pb-0"
                    style={{ borderLeft: `4px solid ${programConfig.color}` }}
                >
                    <Row className="align-items-center">
                        <Col>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <Badge
                                    bg={programConfig.bg}
                                    text={badgeTextFor(programConfig.bg)}
                                    className={programConfig.badgeClass || ''}
                                >
                                    {programConfig.label}
                                </Badge>
                                <Badge bg="secondary">
                                    {getCourseDisplay(assignment)}
                                    {sectionOf(assignment) &&
                                        `-${sectionOf(assignment)}`}
                                </Badge>
                                <Badge
                                    bg={
                                        STATUS_CONFIG[assignment.status]?.bg ||
                                        'secondary'
                                    }
                                    text={badgeTextFor(
                                        STATUS_CONFIG[assignment.status]?.bg ||
                                            'secondary'
                                    )}
                                >
                                    {STATUS_CONFIG[assignment.status]?.label ||
                                        assignment.status}
                                </Badge>
                            </div>
                            <h5 className="mb-0">
                                {assignment.course?.title ||
                                    'Course Assignment'}
                            </h5>
                            {hiringEnabled && assignment.section ? (
                                <div className="small text-muted">
                                    {assignment.section.section && (
                                        <span className="me-3">
                                            Section {assignment.section.section}
                                        </span>
                                    )}
                                    {assignment.section.crn && (
                                        <span className="me-3">
                                            CRN {assignment.section.crn}
                                        </span>
                                    )}
                                    {assignment.section.instructorName && (
                                        <span>
                                            Instructor{' '}
                                            {assignment.section.instructorName}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                assignment.instructorName && (
                                    <small className="text-muted">
                                        {assignment.instructorName}
                                    </small>
                                )
                            )}
                        </Col>
                        <Col xs="auto" className="text-end">
                            {assignment.startDate && assignment.endDate && (
                                <small className="text-muted d-block">
                                    {formatDeadlineDay(assignment.startDate)} -{' '}
                                    {formatDeadlineDay(assignment.endDate)}
                                </small>
                            )}
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {hiringEnabled &&
                        assignment.programType === 'la' &&
                        assignment.sourceCandidacy &&
                        assignment.status !== 'completed' &&
                        assignment.onboarding?.status !== 'completed' && (
                            <p className="border-start border-3 border-info ps-3 mb-3">
                                <span className="fw-semibold">Next: </span>
                                {assignment.onboarding?.emailSentAt
                                    ? `The LA program emailed you onboarding steps on ${formatDeadlineDay(assignment.onboarding.emailSentAt)}. Check your inbox.`
                                    : 'The LA program will contact you about onboarding so you can be hired.'}
                            </p>
                        )}
                    <Row>
                        <Col md={6}>
                            {/* Schedule */}
                            {assignment.schedule?.sessions?.length > 0 && (
                                <>
                                    <h6 className="text-muted mb-2">
                                        Schedule
                                    </h6>
                                    <Table size="sm" className="small mb-3">
                                        <tbody>
                                            {assignment.schedule.sessions.map(
                                                (session, idx) => (
                                                    <tr key={idx}>
                                                        <td className="fw-medium">
                                                            {session.day}
                                                        </td>
                                                        <td>{session.time}</td>
                                                        <td className="text-muted">
                                                            {session.location}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                            {assignment.schedule.officeHours?.map(
                                                (oh, idx) => (
                                                    <tr
                                                        key={`oh-${idx}`}
                                                        className="text-info"
                                                    >
                                                        <td className="fw-medium">
                                                            {oh.day} (Office
                                                            Hours)
                                                        </td>
                                                        <td>{oh.time}</td>
                                                        <td className="text-muted">
                                                            {oh.location}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </Table>
                                </>
                            )}

                            {/* Faculty member (stored as the supervisor) */}
                            {assignment.supervisor && (
                                <>
                                    <h6 className="text-muted mb-2">Faculty</h6>
                                    <p className="small mb-0">
                                        <strong>
                                            {assignment.supervisor.name}
                                        </strong>
                                        {assignment.supervisor.email && (
                                            <>
                                                <br />
                                                <a
                                                    href={`mailto:${assignment.supervisor.email}`}
                                                    className="text-decoration-none"
                                                >
                                                    {
                                                        assignment.supervisor
                                                            .email
                                                    }
                                                </a>
                                            </>
                                        )}
                                    </p>
                                </>
                            )}
                        </Col>
                        <Col md={6}>
                            {notStarted ? (
                                <AssignmentStartNote
                                    assignment={assignment}
                                    startDate={startDate}
                                />
                            ) : (
                                assignment.hoursPerWeek > 0 && (
                                    <>
                                        <h6 className="text-muted mb-2">
                                            Expected time
                                        </h6>
                                        <p className="small mb-3">
                                            ~{assignment.hoursPerWeek}{' '}
                                            hours/week expected
                                        </p>
                                    </>
                                )
                            )}

                            {/* Responsibilities */}
                            {assignment.responsibilities?.length > 0 && (
                                <>
                                    <h6 className="text-muted mb-2">
                                        Responsibilities
                                    </h6>
                                    <ul className="small ps-3 mb-0">
                                        {assignment.responsibilities.map(
                                            (resp, idx) => (
                                                <li key={idx}>{resp}</li>
                                            )
                                        )}
                                    </ul>
                                </>
                            )}
                        </Col>
                    </Row>
                    {/* Nothing to write before the term starts, so the
                        start note stands in for an empty journal */}
                    {(!notStarted || assignment.hourLog?.length > 0) &&
                        renderJournal(assignment, { showNote: true })}
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {!notStarted && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleOpenJournal(assignment)}
                            >
                                Add journal entry
                                {/* Names the course, so several cards' buttons differ */}
                                <span className="visually-hidden">
                                    {' '}
                                    for{' '}
                                    {assignment.course?.title ||
                                        getCourseDisplay(assignment)}
                                </span>
                            </Button>
                        )}
                        <Button variant="outline-secondary" size="sm" disabled>
                            View Resources
                        </Button>
                        {assignment.supervisor?.email && (
                            <Button
                                variant="outline-info"
                                size="sm"
                                as="a"
                                href={`mailto:${assignment.supervisor.email}`}
                            >
                                Contact faculty
                            </Button>
                        )}
                        {assignment.programType === 'la' && (
                            <span className="small text-muted">
                                Questions about your LA position?{' '}
                                {laContactEmail ? (
                                    <a href={`mailto:${laContactEmail}`}>
                                        Contact the LA program
                                    </a>
                                ) : (
                                    <a
                                        href={PROGRAM_PAGE_URLS.la}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Contact the LA program
                                        <span className="visually-hidden">
                                            {' '}
                                            (opens in a new tab)
                                        </span>
                                    </a>
                                )}
                            </span>
                        )}
                    </div>
                </Card.Footer>
            </Card>
        );
    };

    const renderPastAssignmentCard = (assignment) => {
        const programConfig =
            PROGRAM_CONFIG[assignment.programType] || PROGRAM_CONFIG.si;

        return (
            <Card key={assignment._id} className="mb-3 border-0 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <Badge
                                    bg={programConfig.bg}
                                    text={badgeTextFor(programConfig.bg)}
                                    className={programConfig.badgeClass || ''}
                                >
                                    {programConfig.label}
                                </Badge>
                                <Badge bg="secondary">
                                    {getCourseDisplay(assignment)}
                                </Badge>
                            </div>
                            <h6 className="mb-1">
                                {assignment.course?.title ||
                                    'Course Assignment'}
                            </h6>
                        </Col>
                        <Col md={3} className="text-center">
                            <div className="mb-1">
                                <span className="h4 text-success">
                                    {assignment.hourLog?.length || 0}
                                </span>
                            </div>
                            <small className="text-muted">
                                Journal Entries
                            </small>
                        </Col>
                        <Col md={3} className="text-center">
                            {assignment.performanceRating && (
                                <>
                                    <div className="mb-1">
                                        <span className="h4 text-primary">
                                            {assignment.performanceRating}
                                        </span>
                                        <small className="text-muted">
                                            {' '}
                                            / 5.0
                                        </small>
                                    </div>
                                    <small className="text-muted">
                                        Performance Rating
                                    </small>
                                </>
                            )}
                        </Col>
                    </Row>
                    {assignment.hourLog?.length > 0 &&
                        renderJournal(assignment, { showNote: false })}
                    {assignment.feedback && (
                        <div className="mt-3 p-2 bg-light rounded">
                            <small className="text-muted d-block mb-1">
                                Feedback:
                            </small>
                            <p className="small mb-0 fst-italic">
                                &ldquo;{assignment.feedback}&rdquo;
                            </p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    const currentAssignments = assignments.current || [];
    const pastAssignments = assignments.past || [];

    const groupedByTerm = pastAssignments.reduce((acc, assignment) => {
        const term = assignment.term || 'Unknown Term';
        if (!acc[term]) {
            acc[term] = [];
        }
        acc[term].push(assignment);
        return acc;
    }, {});

    const sortedTerms = Object.keys(groupedByTerm).sort((a, b) => {
        const aStart = termMap[a]?.startDate
            ? new Date(termMap[a].startDate)
            : new Date(0);
        const bStart = termMap[b]?.startDate
            ? new Date(termMap[b].startDate)
            : new Date(0);
        return bStart - aStart;
    });

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>My Assignments</h1>
                    <p className="text-muted">
                        View and manage your peer support assignments
                    </p>
                </div>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Stats Overview */}
            <Row className="mb-4">
                <Col md={4} className="mb-3 mb-md-0">
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body>
                            <h3 className="text-primary">
                                <StatValue
                                    loading={loading}
                                    failed={loadFailed}
                                    value={currentAssignments.length}
                                />
                            </h3>
                            <small className="text-muted">
                                Active Assignments
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4} className="mb-3 mb-md-0">
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body>
                            <h3 className="text-success">
                                <StatValue
                                    loading={loading}
                                    failed={loadFailed}
                                    value={journalCount(currentAssignments)}
                                />
                            </h3>
                            <small className="text-muted">
                                Journal Entries This Term
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center h-100">
                        <Card.Body>
                            <h3 className="text-info">
                                <StatValue
                                    loading={loading}
                                    failed={loadFailed}
                                    value={pastAssignments.length}
                                />
                            </h3>
                            <small className="text-muted">
                                Past Assignments
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {loading ? (
                <LoadingState label="Loading assignments" />
            ) : loadFailed ? (
                <LoadError
                    what="your assignments"
                    onRetry={() => setReloadKey((k) => k + 1)}
                />
            ) : (
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4"
                >
                    <Tab
                        eventKey="current"
                        title={`Current (${currentAssignments.length})`}
                    >
                        {currentAssignments.length === 0 ? (
                            <EmptyState
                                message="You don't have any active assignments this term."
                                action={
                                    findWorkHref === '/positions'
                                        ? {
                                              to: '/positions',
                                              label: 'Browse open positions',
                                          }
                                        : {
                                              to: '/courses',
                                              label: 'Browse Courses',
                                          }
                                }
                            />
                        ) : (
                            currentAssignments.map(renderCurrentAssignmentCard)
                        )}
                    </Tab>
                    <Tab
                        eventKey="past"
                        title={`Past (${pastAssignments.length})`}
                    >
                        {pastAssignments.length === 0 ? (
                            <EmptyState message="No past assignments. Assignments from earlier terms are kept here." />
                        ) : (
                            sortedTerms.map((term) => (
                                <div key={term} className="mb-4">
                                    <h6 className="text-muted mb-3">
                                        {termMap[term]?.name || term}
                                    </h6>
                                    {groupedByTerm[term].map(
                                        renderPastAssignmentCard
                                    )}
                                </div>
                            ))
                        )}
                    </Tab>
                </Tabs>
            )}

            {/* Help Section */}
            <Card className="mt-4 bg-light border-0">
                <Card.Body>
                    <h5 className="fw-bold mb-3">Assignment Information</h5>
                    <Accordion flush>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                What are my responsibilities?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                <p>
                                    Your responsibilities depend on your
                                    program:
                                </p>
                                <ul>
                                    <li>
                                        <strong>SI Leaders:</strong> Facilitate
                                        group study sessions, prepare materials,
                                        track attendance, and attend program
                                        meetings.
                                    </li>
                                    <li>
                                        <strong>Teaching Assistants:</strong>{' '}
                                        Support the instructor with grading,
                                        office hours, lab sessions, and course
                                        administration.
                                    </li>
                                    <li>
                                        <strong>Learning Assistants:</strong>{' '}
                                        Support active learning during class,
                                        help students with problem-solving, and
                                        attend pedagogy training.
                                    </li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                What is the activity journal?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                Click &ldquo;Add journal entry&rdquo; on any
                                current assignment to note the date, what you
                                did, and what went well if you like. Keep it for
                                your own reference. {NOT_A_TIME_CARD}
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>
                                Who do I contact if I have questions?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                <p>
                                    Contact your faculty member first for
                                    questions about the course. For
                                    program-related questions, contact your
                                    program coordinator:
                                </p>
                                <ul>
                                    <li>
                                        <strong>SI Program:</strong> Visit the{' '}
                                        <Link to="/about#si">SI page</Link> for
                                        contact information
                                    </li>
                                    <li>
                                        <strong>TA Program:</strong> Visit the{' '}
                                        <Link to="/about#ta">TA page</Link> for
                                        contact information
                                    </li>
                                    <li>
                                        <strong>LA Program:</strong> Visit the{' '}
                                        <Link to="/about#la">LA page</Link> for
                                        contact information
                                    </li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Card.Body>
            </Card>
            {/* Journal Entry Modal */}
            <Modal
                aria-labelledby="asg-modal-title-1"
                show={showJournalModal}
                onHide={closeJournal}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="asg-modal-title-1">
                        Add journal entry
                    </Modal.Title>
                </Modal.Header>
                <Form noValidate onSubmit={handleSubmitJournal}>
                    <Modal.Body>
                        {journalAssignment && (
                            <p className="fw-medium mb-1">
                                {journalAssignment.course?.title ||
                                    'Course Assignment'}
                            </p>
                        )}
                        <p className="small text-muted mb-3">
                            {NOT_A_TIME_CARD}
                        </p>
                        {journalError && (
                            <Alert variant="danger" className="mb-3">
                                {journalError}
                            </Alert>
                        )}
                        <Form.Group controlId="journalDate" className="mb-3">
                            <Form.Label>Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={journalForm.date}
                                onChange={updateJournalForm('date')}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="journalNotes" className="mb-3">
                            <Form.Label>What did you do?</Form.Label>
                            <Form.Control
                                ref={notesRef}
                                as="textarea"
                                rows={3}
                                maxLength={2000}
                                placeholder="e.g., Helped a group work through the lab in class"
                                value={journalForm.notes}
                                onChange={updateJournalForm('notes')}
                                isInvalid={notesMissing}
                                aria-invalid={notesMissing || undefined}
                                aria-describedby={
                                    notesMissing
                                        ? 'journalNotesError'
                                        : undefined
                                }
                                required
                            />
                            <Form.Control.Feedback
                                id="journalNotesError"
                                type="invalid"
                            >
                                Say what you did.
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                            controlId="journalWentWell"
                            className="mb-3"
                        >
                            <Form.Label>What went well? (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                maxLength={2000}
                                value={journalForm.wentWell}
                                onChange={updateJournalForm('wentWell')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-primary"
                            onClick={closeJournal}
                            disabled={savingEntry}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={savingEntry}
                        >
                            {savingEntry ? 'Saving...' : 'Save entry'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}

export default AssignmentsPage;
